import { useState } from "react";
import { Mail, PhoneCall, MessageCircle, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { waLink, WHATSAPP_NUMBER } from "@/lib/config";
import { Reveal } from "@/components/motion";

const FIELD = "w-full border border-slate-200 bg-white px-4 py-3 text-sm rounded-xl focus:outline-none focus:border-emerald-600 transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", business: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", business: "", email: "", phone: "", message: "" });
      toast.success("Enquiry sent! Our team will reach out shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const channels = [
    { icon: Mail, title: "Mail Us", value: "info@kananremedies.com", href: "mailto:info@kananremedies.com", testid: "contact-email-card", note: "Replies within 1 business day" },
    { icon: PhoneCall, title: "Call Us", value: "+91 8852832320", href: `tel:+${WHATSAPP_NUMBER}`, testid: "contact-call-card", note: "Mon–Sat, 10 AM – 7 PM IST" },
    { icon: MessageCircle, title: "WhatsApp", value: "+91 8852832320", href: waLink("Hello Kanan Remedies, I have an enquiry."), testid: "contact-whatsapp-card", note: "Fastest response for orders" },
  ];

  return (
    <div className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-32 pb-20" data-testid="contact-page">
      <Reveal>
        <p className="font-mono-ed text-[11px] uppercase tracking-[0.3em] text-emerald-700 mb-4">Get In Touch</p>
        <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Contact Us</h1>
        <p className="text-base text-slate-600 mt-4 max-w-xl">Questions about products, pricing, schemes or your order? Reach us on any channel — or drop a message and we'll call you back.</p>
      </Reveal>

      <div className="grid sm:grid-cols-3 gap-4 mt-10">
        {channels.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.08}>
            <a
              href={c.href}
              target={c.href.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              data-testid={c.testid}
              className="block bg-white border border-slate-200 rounded-2xl p-6 hover:border-emerald-400 hover:shadow-lg transition-all duration-300 group"
            >
              <span className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center group-hover:bg-emerald-700 group-hover:text-white transition-colors duration-300">
                <c.icon size={20} />
              </span>
              <p className="font-bold text-slate-900 mt-4">{c.title}</p>
              <p className="font-mono-ed text-sm text-emerald-800 mt-1 break-all">{c.value}</p>
              <p className="text-[11px] text-slate-400 mt-1.5">{c.note}</p>
            </a>
          </Reveal>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-6 mt-10">
        <Reveal className="lg:col-span-3">
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-7 sm:p-9" data-testid="contact-form">
            <h2 className="font-display font-bold text-xl">Send an Enquiry</h2>
            <p className="text-xs text-slate-400 mt-1.5">For bulk pricing, distribution or product queries.</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <input data-testid="contact-name-input" className={FIELD} placeholder="Your name *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input data-testid="contact-business-input" className={FIELD} placeholder="Pharmacy / business name" value={form.business} onChange={(e) => setForm({ ...form, business: e.target.value })} />
              <input data-testid="contact-email-input" className={FIELD} type="email" placeholder="Email *" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input data-testid="contact-phone-input" className={FIELD} placeholder="Phone / WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <textarea data-testid="contact-message-input" className={`${FIELD} sm:col-span-2`} rows={4} placeholder="Your message *" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <button
              data-testid="contact-submit-btn"
              disabled={busy}
              className="mt-6 bg-emerald-800 hover:bg-emerald-900 text-white px-9 py-3.5 rounded-xl font-bold text-sm shadow-md transition-all duration-300 active:scale-95 disabled:opacity-50 flex items-center gap-2"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : sent ? <CheckCircle2 size={15} /> : null}
              {sent ? "Sent — We'll be in touch" : "Send Message"}
            </button>
          </form>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-2">
          <div className="bg-emerald-900 rounded-2xl p-7 sm:p-9 h-full">
            <h3 className="font-display font-bold text-xl text-white">Registered Office</h3>
            <p className="flex items-start gap-2.5 text-sm text-emerald-100/85 leading-relaxed mt-4">
              <MapPin size={16} className="text-amber-400 mt-0.5 shrink-0" />
              B-316, Crystal Plaza, Opp. Infinity Mall, Link Road, Andheri West, Mumbai-400053
            </p>
            <div className="border-t border-white/10 mt-6 pt-6 space-y-2.5 font-mono-ed text-xs text-emerald-100/70">
              <p>DL No.: 24-25/118778-79</p>
              <p>GST No.: 08CEDPS6160N1ZW</p>
              <p className="text-emerald-100/50">WHO-GMP Certified · Strictly B2B Trade</p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
